import * as React from "react";
import * as ReactDOM from "react-dom";
import { observable } from "mobx";
import { observer } from "mobx-react";
import { Button } from "./Button";
import { IButtonProps } from "./IButtonProps";

interface IButtonGroupProps {
	/**
  * Identifies ButtonGroup reactive(responsive) or not
  */
	isReactiveMenu?: boolean;
	/**
  * Drag event Detection(From AreaAside Component) and changes width accordingly
  */
	asideWidth?: number;
}
/**
* Stores id and width of buttons in ButtonGroup
*/
type ButtonStore = {
	id: string;
	width: number;
}


/**
  * Class ButtonGroup
  */
@observer
export class ButtonGroup extends React.Component<IButtonGroupProps, {}> {
	/**
  * Stores toolbar width on every resize
  */
	containerWidth: number = 0;
	/**
  * Stores is Mobile View or not
  */
	isMobile: boolean = false;
	/**
  * Stores sorted buttons
  */
	@observable sortedButtons: (React.ReactNode | string | number)[] = [];
	/**
  * Stores buttons from ButtonGroup
  */
	@observable panelButtons: (React.ReactNode | string | number)[] = [];
	/**
  * Stores buttons from ButtonGroup
  */
	@observable initialButtons: (React.ReactNode | string | number)[] = [];
	/**
  * Stores width and id of buttongroup buttons.
  */
	buttonArray: ButtonStore[] = [];
	initialbuttonArray: ButtonStore[] = [];

	componentWillMount() {
		this.initButtons();
	}
	/**
  * copies buttongroup buttons to arrays:
  * 1) panelButtons
  * 2) initialButtons
  */
	private initButtons = (): void => {
		this.panelButtons = React.Children.toArray(this.props.children);
		this.initialButtons = React.Children.toArray(this.props.children);
		this.sortedButtons = [];
	}

	componentDidMount() {
		//adding resize event and callback function
		window.addEventListener("resize", this.updateDimensions);
		if (this.props.isReactiveMenu) {
			//checks Reactive  buttongroup the do the 2 operations
			this.getButtonCollection();
			this.initialbuttonArray = this.buttonArray;
			let isMobileWidth: number = 0;
			this.buttonArray.forEach((item) => {
				isMobileWidth += item.width;
			});
			this.isMobile = (isMobileWidth == this.buttonArray.length * 26) ? true : false;
			this.updateDimensions();
		}
	}

	componentDidUpdate(nextProps: IButtonGroupProps) {
		//Listen to Props change and invoke updateDimensions (ex: AsideWidth change)
		if (this.props.isReactiveMenu && this.props !== nextProps) {
			this.updateDimensions();
		}
	}

	componentWillUnmount() {
		//Remove resize event and callback function
		window.removeEventListener("resize", this.updateDimensions);
	}

	private updateDimensions = (): void => {
		//check for reactive buttongroup or not
		if (this.props.isReactiveMenu) {
			//calculate main container width
			this.containerWidth = this.getContainerWidth();
			//initilize buttons
			this.initButtons();
			this.sortedButtons = [];
			let buttonArrayTotalWidth: number = this.getButtonsTotalWidth();

			//checks whether the container width is less than total width of buttons for converting buttons to reactive
			if ((this.containerWidth < buttonArrayTotalWidth)) {
				//create a temp array from button array for further calculation
				let tempButtonarray: ButtonStore[] = this.initialbuttonArray;
				while ((this.containerWidth < buttonArrayTotalWidth) && buttonArrayTotalWidth > 0) {
					//sorting initial array
					this.sortInitialButtons();
					if (this.initialButtons.length > 0) {
						//getting least prioty button nad its width
						let leastpriorButton: React.ReactElement<Button> = this.getLeastPriorButton();
						let leastpriorButtonWidth: number = this.getLeastPriotButtonWidth(leastpriorButton);

						//remove button from temp array
						tempButtonarray = tempButtonarray.filter((item) => {
							let buttonProps: IButtonProps = leastpriorButton.props as IButtonProps;
							return (item.id !== buttonProps.id)
						});

						//find width of remainig buttons
						if (window.innerWidth <= 479) {
							buttonArrayTotalWidth -= 26;
						}

						if (window.innerWidth > 479) {
							buttonArrayTotalWidth = 0;
							tempButtonarray.forEach((item) => {
								buttonArrayTotalWidth += item.width;
							});
						}

						this.addButtonTosorted();
						this.removeButtonsFromArray(leastpriorButton);
					}
				}
			}
			//sort array
			this.sortButtons();
		}
	}

	private getButtonsTotalWidth = () => {
		let buttonArrayTotalWidth: number = 0;
		if (window.innerWidth <= 479) {
			this.isMobile = true;
			buttonArrayTotalWidth = this.initialbuttonArray.length * 26;
		}
		if (window.innerWidth > 479) {
			this.getButtonCollection();
			if (this.isMobile) { this.initialbuttonArray = this.buttonArray; this.isMobile = false; }
			buttonArrayTotalWidth = 0;
			this.initialbuttonArray.forEach((item) => {
				buttonArrayTotalWidth += item.width;
			});
		}

		return buttonArrayTotalWidth;
	}

	private getContainerWidth = () => {
		return ReactDOM.findDOMNode(this).parentElement.clientWidth - 100;
	}


	private addButtonTosorted = () => {
		//add button to sorted 
		this.sortedButtons.push(this.initialButtons[this.initialButtons.length - 1]);
	}

	private getLeastPriorButton = () => {
		return this.initialButtons[this.initialButtons.length - 1] as React.ReactElement<Button>
	}

	private getLeastPriotButtonWidth = (leastpriorButton: React.ReactElement<Button>) => {
		let leastpriorButtonWidth: number = 0;
		this.buttonArray.forEach((item: ButtonStore) => {
			let buttonProps: IButtonProps = leastpriorButton.props as IButtonProps;
			if (item.id == buttonProps.id) {
				leastpriorButtonWidth = item.width;
			}
		});
		return leastpriorButtonWidth;
	}


	private removeButtonsFromArray = (leastpriorButton: React.ReactElement<Button>) => {
		//remove button from panelButtons and initialButtons arrays
		this.initialButtons.pop();
		this.panelButtons = this.panelButtons.filter((button: React.ReactElement<Button>) => {
			let leastpriorButtonProps: IButtonProps = leastpriorButton.props as IButtonProps;
			let buttonProps: IButtonProps = button.props as IButtonProps
			return leastpriorButtonProps.id !== buttonProps.id;
		});
	}
	/**
  * Get all buttons from DOM and stores it id and width.
  */
	private getButtonCollection = (): void => {
		let visibleButtons = this.refs.visibleButtons.getElementsByTagName("button");
		this.buttonArray = [];
		for (let i = 0; i < visibleButtons.length; i++) {
			this.buttonArray.push({ id: visibleButtons.item(i).id, width: visibleButtons.item(i).clientWidth });
		}
	}
	/**
  * Sorts `initialButtons` Array, for finding least prior button. that's why we keeps 2 arrays for storing buttons(panelButtons and initialButtons).
  */
	private sortInitialButtons = (): void => {
		this.initialButtons = this.initialButtons.sort((buttonA: React.ReactElement<Button>, buttonB: React.ReactElement<Button>) => {
			let buttonPropsA: IButtonProps = buttonA.props as IButtonProps,
				buttonPropsB: IButtonProps = buttonB.props as IButtonProps;
			if (!buttonPropsA.prioritization) {
				return 1;
			}
			if (!buttonPropsB.prioritization) {
				return -1;
			}
			return buttonPropsA.prioritization - buttonPropsB.prioritization;
		});
	}
	/**
  * Sorts `sortedButtons` Array, for render in sub menu depends on priority.
  */
	private sortButtons = (): void => {
		this.sortedButtons = this.sortedButtons.sort((buttonA: React.ReactElement<Button>, buttonB: React.ReactElement<Button>) => {
			let buttonPropsA: IButtonProps = buttonA.props as IButtonProps,
				buttonPropsB: IButtonProps = buttonB.props as IButtonProps;
			if (!buttonPropsA.prioritization) {
				return 1;
			}
			if (!buttonPropsB.prioritization) {
				return -1;
			}
			return buttonPropsA.prioritization - buttonPropsB.prioritization;
		});
	}

	refs: {
		[key: string]: HTMLElement,
		/**
    * reference name for getting Buttons from DOM.
    */
		visibleButtons: HTMLElement
	}


	render() {
		let buttonGroupClass = this.props.isReactiveMenu ? "buttongroup is--more-menu" : "buttongroup";
		return <div className={buttonGroupClass} ref="visibleButtons">
			<span>
				{this.props.isReactiveMenu ? this.panelButtons : this.props.children}
			</span>
			{
				this.props.isReactiveMenu && this.sortedButtons.length > 0 ?
					<div className="buttongroup-more">
						<span>...</span>
						<div className="buttongroup-more-menu">{this.sortedButtons}</div>
					</div> : null
			}
		</div>
	}
}